import type { MultiVoucherItemType } from 'app/src/features/ProductDetail/components/CounterPrice'
import { ProductActivationModal } from 'app/src/features/ProductDetail/components/ProductActivationModal'
import { ProductDiscountDisplay } from 'app/src/features/ProductDetail/components/ProductDiscountDisplay'
import { ProductHeader } from 'app/src/features/ProductDetail/components/ProductHeader'
import { ProductImageCarousel } from 'app/src/features/ProductDetail/components/ProductImageCarousel'
import { ProductInformativeMetadata } from 'app/src/features/ProductDetail/components/ProductInformativeMetadata'
import { ProductMultiPurchase } from 'app/src/features/ProductDetail/components/ProductMultiPurchase'
import { ProductVariantList } from 'app/src/features/ProductDetail/components/ProductVariantList'
import {
  catalogSummaryApi,
  companyagreementApi,
  membershipApi,
  PrivacyDocument,
  shoppingApi,
  TargetPersonEnum,
  type UserFamilyMember,
} from 'app/src/services/welfareportal'
import type { PrivacyConsentEntry } from 'app/src/services/welfareportal/shopping-api/shopping-schemas'
import { InformativeBanner, PageWidget, PortalDialog } from 'app/src/shared/components'
import { euroFormatterIt, getFromSessionStorage } from 'app/src/shared/helpers'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import { Parser } from 'html-to-react'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import type { SelectItem } from 'primereact/selectitem'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export function ProductDetail(props: {
  macroCategoryId: string
  productId: string
  parentSelectedCategoryItem?: companyagreementApi.Category
  apiConfiguration?: companyagreementApi.ApiConfiguration
  setProductName: React.Dispatch<React.SetStateAction<string | null>>
}): ReactElement {
  const context = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const { t } = useTranslation('productDetail')
  const [selectedVariant, setSelectedVariant] = useState<companyagreementApi.Variant>()
  const [productDetailsHtml, setProductDetailsHtml] = useState<string>()
  const [multiPurchaseTotal, setMultiPurchaseTotal] = useState<number>(0)
  const [selectedPriceVoucher, setSelectedPriceVoucher] = useState<
    MultiVoucherItemType<companyagreementApi.Variant | undefined>[]
  >([])
  const [productActivationModalVisible, setProductActivationModalVisible] = useState<boolean>(false)
  const [confirmDisabled, setConfirmDisabled] = useState<boolean>(true)
  const [familyMemberOptionValue, setFamilyMemberOptionValue] = useState<SelectItem[]>([])
  const [userSelected, setUserSelected] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<JSX.Element>()
  const [errorModalVisible, setErrorModalVisible] = useState<boolean>(false)
  const [finalUsage, setFinalUsage] = useState<string>()
  const [finalUsageIsSelected, setFinalUsageIsSelected] = useState<boolean>(false)
  const [privacyItems, setPrivacyItems] = useState<{ [key: string]: PrivacyConsentEntry }>({})
  const [activateResult, setActivateResult] = useState<shoppingApi.ActivateResult | undefined>()
  const [formRef] = useState(useRef<HTMLFormElement>(null))
  const [productActivationModalConfirmDisabled, setProductActivationModalConfirmDisabled] = useState<boolean>(true)
  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'
  const [multivoucherSelectedVariantUsableAt, setMultivoucherSelectedVariantUsableAt] = useState<
    { [key: string]: string } | undefined
  >(undefined)

  const membershipResponse = !context.userInfo.isForbidden
    ? membershipApi.useMembershipGet({
        pathParams: { membershipId: JSON.parse(campaignStorage).membershipId ?? '' },
        queryParams: { includeFamilyMembers: true, includeAdditionalInfo: true },
      })
    : undefined

  const catalogSummaryDetails = catalogSummaryApi.useCatalogSummaryList({
    pathParams: { templateBasePath: context.userInfo.welfareCampaign.configuration.frontend.templateId },
  })

  const productResponse = companyagreementApi.useProductGet<companyagreementApi.Product>({
    pathParams: { productId: props.productId }, //{ productId: props.productId }, // { productId: 'CQ15WEGS' }, // { productId: '2104L5CB' }//(6 variants) // { productId: '4H03VAEB' }//4 variants
    queryParams: { includeVariants: true },
  })

  const confirmBtnText = useMemo(() => {
    return t('ProductDetail.typologies')[productResponse.data?.typology ?? 'pass'].ButtonLabel ?? ''
  }, [productResponse.data, t])

  const productImages = useMemo(() => {
    const parentCategoryMainImage = props.parentSelectedCategoryItem?.mainImage
      ? props.parentSelectedCategoryItem?.mainImage
      : undefined
    return productResponse.data?.images && productResponse.data.images.length > 0
      ? productResponse.data.images
      : parentCategoryMainImage
        ? [parentCategoryMainImage]
        : []
  }, [productResponse.data, props.parentSelectedCategoryItem])

  useEffect(() => {
    setSelectedVariant(
      productResponse.data?.variants && productResponse.data.variants.length > 0 ? productResponse.data.variants[0] : undefined,
    )

    if (productResponse.data?.productName) props.setProductName(productResponse.data?.productName)

    if (productResponse.data?.details) {
      const workerFunc = async () => {
        const v = await (await fetch(productResponse.data.details!)).text()
        setProductDetailsHtml(v)
      }
      workerFunc()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productResponse.data])

  useEffect(() => {
    let disabled = !membershipResponse?.data
    if (!disabled && productResponse.data && productResponse.data.multiPurchase) {
      disabled = !selectedPriceVoucher || selectedPriceVoucher.length === 0
    } else {
      if (!disabled && productResponse.data) {
        disabled = !selectedVariant || !selectedVariant?.active
      }
    }

    setConfirmDisabled(disabled)
  }, [membershipResponse?.data, productResponse.data, selectedPriceVoucher, selectedVariant])

  const serviceCategoryTag: string | undefined = useMemo(() => {
    if (
      !catalogSummaryDetails.isError &&
      !productResponse.isError &&
      catalogSummaryDetails.data &&
      catalogSummaryDetails.data.categories &&
      productResponse?.data
    ) {
      let macroCategoryId = props.macroCategoryId

      if (!macroCategoryId) {
        const categoryIds = productResponse?.data?.categoryIds
        macroCategoryId = categoryIds?.[0]?.split('.')?.[0] || ''
      }

      return catalogSummaryDetails.data.categories
        .find((x) => x.id === macroCategoryId)
        ?.serviceCategoryConfigurations?.find((x) => x.key === productResponse.data.serviceCategory)?.tabberName
    }

    return 'none'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogSummaryDetails, catalogSummaryDetails, productResponse, productResponse, props.macroCategoryId])

  const onChangeSelectedVariant = useCallback((v?: companyagreementApi.Variant) => {
    setSelectedVariant(v)
  }, [])

  useEffect(() => {
    if (familyMemberOptionValue.find((x) => x.value?.familyMemberId === membershipResponse?.data?.membershipInfo?.userId))
      setUserSelected(true)
    else setUserSelected(false)
  }, [familyMemberOptionValue, membershipResponse?.data?.membershipInfo?.userId])

  const familyMemberSelected: SelectItem[] = useMemo(
    () =>
      familyMemberOptionValue.length !== 0
        ? familyMemberOptionValue.filter((x) => x.value?.familyMemberId !== membershipResponse?.data?.membershipInfo?.userId)
        : [],
    [familyMemberOptionValue, membershipResponse?.data?.membershipInfo?.userId],
  )

  useEffect(() => {
    if (activateResult) {
      try {
        if (activateResult.method === 'POST') {
          if (formRef?.current) {
            formRef.current.submit()
          } else {
            context.logger?.error('Error occurred with Form')
            throw new Error('Activating Form Error')
          }
        } else {
          if (activateResult.method === 'GET') {
            if (activateResult.target) {
              window.open(activateResult.url, activateResult.target)
            } else {
              window.open(activateResult.url, '_self')
            }
          }
        }
      } catch (err) {
        context.logger?.error('Error activating product', { error: err })
        context.showToastMessage({ content: 'Error activating product', severity: 'error' })
      } finally {
        setProductActivationModalVisible(false)
        resetProductActivationModal()
        setActivateResult(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateResult])

  const validateCfMutation = membershipApi.useUsersValidateFiscalCode()

  const validateCfMutationWorker = useCallback(
    async (cfToValidate: string) => {
      const validateCfMutationResp = await validateCfMutation.mutateAsync({
        body: { fiscalCode: cfToValidate?.toUpperCase() },
      })
      return validateCfMutationResp.isValid && typeof validateCfMutationResp.idAdult !== 'undefined'
        ? validateCfMutationResp.idAdult
        : false
    },
    [validateCfMutation],
  )

  const maxVoucherCount = useMemo(
    () => context.portalConfiguration.showCaseConfig?.maxVoucherCount ?? 10,
    [context.portalConfiguration?.showCaseConfig?.maxVoucherCount],
  )

  const maxVoucherTypeCount = useMemo(
    () => context.portalConfiguration.showCaseConfig?.maxVoucherTypeCount ?? 5,
    [context.portalConfiguration?.showCaseConfig?.maxVoucherTypeCount],
  )

  const openProductActivationModal = useCallback(() => {
    if (productResponse.data?.multiPurchase) {
      let sumTot = 0

      let errorOccurred: boolean = selectedPriceVoucher?.some((priceItem) => priceItem.quantity > maxVoucherTypeCount)
      if (!errorOccurred) {
        selectedPriceVoucher?.forEach((priceItem) => (sumTot += priceItem.quantity))
        errorOccurred = sumTot > maxVoucherCount
      }
      if (errorOccurred) {
        setErrorMsg(
          <div>
            {t('ProductDetail.errorModal.errorMultipurchase', {
              MAXVOUCHERTYPECOUNT: maxVoucherTypeCount,
              MAXVOUCHERCOUNT: maxVoucherCount,
            })}
          </div>,
        )
        setErrorModalVisible(true)
      } else setProductActivationModalVisible(true)
    } else setProductActivationModalVisible(true)
  }, [productResponse.data?.multiPurchase, selectedPriceVoucher, maxVoucherTypeCount, maxVoucherCount, t])

  const onErrorActivate = useCallback((err: shoppingApi.ActivateError | shoppingApi.MultiActivateError) => {
    const showErrorPopup = (msg: JSX.Element) => {
      setProductActivationModalVisible(false)
      resetProductActivationModal()
      setErrorMsg(msg)
      setErrorModalVisible(true)
    }
    if (err.status === 400 || err.status === 409) {
      if (err.payload?.type?.endsWith(':UserMessage')) {
        showErrorPopup(<div>{err.payload?.title}</div>)
      } else {
        if (err.status == 409 && err?.payload?.type === 'urn:CreditInsufficient') {
          showErrorPopup(<div>Attenzione, conto welfare non sufficiente per procedere con l’operazione</div>)
        } else {
          showErrorPopup(<div>Errore Generico</div>)
        }
      }
    } else {
      showErrorPopup(<div>Errore Generico</div>)
    }
  }, [])

  const multiActivateMutation = shoppingApi.useMultiActivate({
    onError: (err) => {
      onErrorActivate(err)
    },
  })

  const multiActivateMutationWorker = useCallback(
    async (request: shoppingApi.MultiActivateRequest) => {
      // try {
      const resp: shoppingApi.ActivateResult = await multiActivateMutation.mutateAsync({
        body: request,
      })
      return resp
      // } catch (er) {
      //   context.logger?.error('Error Multi Activate Product', { error: er })
      //   context.showToastMessage({ content: 'Error Multi Activate Product', severity: 'error' })
      // }
    },
    [multiActivateMutation],
  )

  const singleActivateMutation = shoppingApi.useActivate({
    onError: (err) => {
      onErrorActivate(err)
    },
  })

  const singleActivateMutationWorker = useCallback(
    async (request: shoppingApi.ActivateRequest) => {
      // try {
      const resp: shoppingApi.ActivateResult = await singleActivateMutation.mutateAsync({
        body: request,
      })
      return resp
      // } catch (er) {
      //   context.logger?.error('Error Single Activate Product', { error: er })
      //   context.showToastMessage({ content: 'Error Single Activate Product', severity: 'error' })
      // }
    },
    [singleActivateMutation],
  )

  function getFinalUsageList(
    variant?: companyagreementApi.Variant,
    apiConfiguration?: companyagreementApi.ApiConfiguration,
  ): string[] {
    if (!variant || !apiConfiguration) return []
    else
      return (
        (apiConfiguration.merchandiseCategory &&
        variant &&
        variant.additionalInfo &&
        variant.additionalInfo?.['merchandiseCategory']
          ? apiConfiguration.merchandiseCategory?.[variant.additionalInfo['merchandiseCategory']]
          : []) ?? []
      )
  }

  const onChangeFinalUsage = (item: SelectItem): void => {
    if (item) {
      setFinalUsage(item.value)
      setFinalUsageIsSelected(true)
    }
  }

  function resetProductActivationModal() {
    setFamilyMemberOptionValue([])
    setUserSelected(false)
    setFinalUsage(undefined)
    setFinalUsageIsSelected(false)
    setPrivacyItems({})
  }

  const renderAnagraficaBtn = (btnLabel: string) => {
    return (
      <Button
        link
        text
        pt={{ root: { className: 'underline p-0' } }}
        onClick={() => {
          navigate.goToUserInfo()
          setProductActivationModalVisible(false)
        }}
      >
        {btnLabel}
      </Button>
    )
  }

  const onConfirmProductActivationModal = async () => {
    setProductActivationModalConfirmDisabled(true)
    if (selectedVariant?.targetPerson && selectedVariant?.targetPerson === TargetPersonEnum.AllAndAdultWithPrivacy.toString()) {
      const notPrivacyFileUsers = familyMemberSelected?.filter(
        (x: SelectItem) => !x?.value?.privacyDocument || x.value?.privacyDocument.status !== PrivacyDocument.StatusEnum.Uploaded,
      )

      for (let i = 0; i < notPrivacyFileUsers.length; i++) {
        // try {
        const validateCF: boolean = await validateCfMutationWorker(notPrivacyFileUsers[i].value?.fiscalCode)
        if (validateCF) {
          const beneficiaryCount = (familyMemberSelected?.length ?? 0) + (userSelected ? 1 : 0)
          setErrorMsg(
            <div>
              {t('ProductDetail.errorModal.p1')}{' '}
              {beneficiaryCount > 1 ? t('ProductDetail.errorModal.p2') : t('ProductDetail.errorModal.p3')}{' '}
              {t('ProductDetail.errorModal.p4')} {renderAnagraficaBtn(t('ProductDetail.errorModal.btnAnagraficaSectionLabel'))}{' '}
              {t('ProductDetail.errorModal.p5')}
            </div>,
          )
          setProductActivationModalVisible(false)
          resetProductActivationModal()
          setErrorModalVisible(true)
          return
        } else setErrorMsg(undefined)
        // } catch {
        //   commonProps.closeModal()
        //   commonProps.showErrorModal(false, GenericErrorMessage)
        //   return
        // }
      }
    }
    let productAdditionalInfo: shoppingApi.ProductAdditionalInfo = {}
    if (finalUsage) {
      productAdditionalInfo = { merchandiseCategory: finalUsage }
    }

    const familyMembersSelection: UserFamilyMember[] = familyMemberSelected.map((x) => x.value)

    if (!selectedVariant || !productResponse.data) {
      throw new Error('Non è stato possibile attivare il prodotto, dati mancanti.')
    }

    const getVariant = (variant: companyagreementApi.Variant | undefined): shoppingApi.ActivateRequest['variant'] => {
      if (!variant) {
        throw new Error('Non è stato possibile attivare il prodotto, variant mancante.')
      }

      return {
        variantId: variant?.variantId,
        externalId: variant?.externalId,
        variantName: variant?.variantName,
        priceDetails: variant?.priceDetails,
        active: variant?.active,
        signature: variant?.signature,
        additionalInfo: variant?.additionalInfo,
      }
    }

    const variant: shoppingApi.ActivateRequest['variant'] = getVariant(selectedVariant)

    const product: shoppingApi.ActivateRequest['product'] = {
      productId: productResponse.data.productId,
      externalId: productResponse.data.externalId,
      typology: productResponse.data.typology,
      requiredAccessLevel: productResponse.data.requiredAccessLevel,
      merchantType: productResponse.data.merchantType,
      serviceCategory: productResponse.data.serviceCategory,
      productName: productResponse.data.productName,
      signature: productResponse.data.signature,
      privacyList: productResponse.data.privacyList,
    }

    const commonBody = {
      product,
      beneficiaries: {
        user: userSelected,
        familyMembers: familyMembersSelection,
      },
      userAdditionalInfoGroup: membershipResponse?.data?.membershipInfo?.additionalInfoGroup,
      productAdditionalInfo,
      privacyConsent: privacyItems,
    }

    if (productResponse.data?.multiPurchase) {
      const bodyReqMulti: shoppingApi.MultiActivateRequest = {
        variants:
          (selectedPriceVoucher
            ?.filter((variant) => variant.quantity > 0)
            ?.sort((a, b) => (a && b ? b!.unitPrice! - a!.unitPrice! : 0))
            ?.map((variant) => ({
              variant: getVariant(variant.payload),
              quantity: variant.quantity,
            })) as shoppingApi.VariantAndQuantity[]) ?? [],
        ...commonBody,
      }
      //TODO GESTIONE ERRORI
      const resp: shoppingApi.ActivateResult | undefined = await multiActivateMutationWorker(bodyReqMulti)
      setActivateResult(resp)
    } else {
      const bodyReqSingle: shoppingApi.ActivateRequest = {
        variant,
        ...commonBody,
      }
      //TODO GESTIONE ERRORI
      const resp: shoppingApi.ActivateResult | undefined = await singleActivateMutationWorker(bodyReqSingle)
      setActivateResult(resp)
    }

    // setDisabled(true)
    // setSpinnerEnabled(true)
    setProductActivationModalConfirmDisabled(false)
  }

  useEffect(() => {
    if (productResponse.data !== undefined) {
      if (productResponse.data.multiPurchase) {
        // trova la prima variante che abbia informativeMetadata popolato
        const firstVariantWithUsableAt = productResponse.data.variants.find(
          (v) => v.informativeMetadata && Object.keys(v.informativeMetadata).length > 0,
        )
        setMultivoucherSelectedVariantUsableAt(firstVariantWithUsableAt?.informativeMetadata)
      }
    }
  }, [productResponse.data, setMultivoucherSelectedVariantUsableAt])

  if (productResponse.isLoading || membershipResponse?.isLoading)
    return (
      <div className='mx-auto flex'>
        <ProgressSpinner data-testid='loading-element' />
      </div>
    )

  const isDiscount = productResponse.data?.typology === 'discount'

  return (
    <PageWidget>
      {activateResult && (
        <form
          ref={formRef}
          action={activateResult?.url}
          target={activateResult?.target}
          method={activateResult?.method?.toString()}
        >
          {activateResult?.payload &&
            Object.keys(activateResult.payload).map((x) => (
              <input key={x} name={x} value={activateResult?.payload?.[x]} type='text' hidden />
            ))}
        </form>
      )}
      {productResponse.data && (
        <div>
          {productResponse.data.additionalInfo && productResponse.data.additionalInfo.informativeMessage && (
            <div className='mb-4 sm:mb-6'>
              <InformativeBanner
                inlineIcon
                text={<div className='text-sm sm:text-base'>{productResponse.data.additionalInfo.informativeMessage}</div>}
              />
            </div>
          )}
          <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-12 lg:gap-3'>
            <div className='mx-auto lg:col-span-5'>
              <ProductImageCarousel images={productImages} />
            </div>
            <div className='lg:col-span-7'>
              <ProductHeader
                title={productResponse.data.productName}
                tag={serviceCategoryTag}
                description={productResponse.data.description}
                isFavourite={productResponse.data?.isFavourite}
                productId={productResponse.data?.productId}
                partnership={productResponse.data?.additionalInfo?.partnership}
                isDiscount={isDiscount}
              />
              {productResponse.data.multiPurchase ? (
                <ProductInformativeMetadata
                  informativeMetadata={multivoucherSelectedVariantUsableAt}
                  multiPurchase={true}
                  variantName={undefined}
                />
              ) : (
                <ProductInformativeMetadata
                  informativeMetadata={selectedVariant?.informativeMetadata}
                  multiPurchase={productResponse.data.multiPurchase}
                  variantName={selectedVariant?.variantName}
                />
              )}

              {isDiscount && (
                <ProductDiscountDisplay
                  multiVariants={productResponse.data.variants?.length > 1}
                  selectedVariant={selectedVariant}
                />
              )}
              {!isDiscount &&
                !productResponse.data.multiPurchase &&
                selectedVariant?.priceDetails &&
                (productResponse.data.typology !== 'goodAndService' || selectedVariant.priceDetails.facePrice !== 0) && (
                  <div className='mt-4 text-left text-2xl font-semibold text-[var(--primary-color)] sm:mt-6 sm:text-3xl lg:mt-[20px] lg:text-[48px]'>
                    {selectedVariant.priceDetails.facePrice === 0
                      ? t('ProductDetail.facePriceGratis')
                      : euroFormatterIt.format(selectedVariant.priceDetails.facePrice)}
                  </div>
                )}
            </div>
            {/* HTML fragment */}
            {productResponse.data?.details && (
              <div className='col-span-1 mt-6 sm:mt-8 lg:col-span-12 lg:mt-[40px]'>{Parser().parse(productDetailsHtml)}</div>
            )}
            {!productResponse.data?.multiPurchase &&
              productResponse.data?.variants &&
              productResponse.data?.variants.length > 0 && (
                <div className='col-span-1 lg:col-span-12'>
                  <ProductVariantList
                    multiPurchase={productResponse.data.multiPurchase}
                    variants={productResponse.data.variants}
                    onChangeSelectedVariant={onChangeSelectedVariant}
                    selectedVariant={selectedVariant}
                  />
                </div>
              )}
            {productResponse.data?.multiPurchase &&
              productResponse.data?.variants &&
              productResponse.data?.variants.length > 0 && (
                <div className='col-span-1 lg:col-span-12'>
                  <ProductMultiPurchase
                    variants={productResponse.data.variants}
                    multiPurchaseTotal={multiPurchaseTotal}
                    setMultiPurchaseTotal={setMultiPurchaseTotal}
                    selectedPriceVoucher={selectedPriceVoucher}
                    setSelectedPriceVoucher={setSelectedPriceVoucher}
                    multiPurchaseDescription={t('ProductDetail.multiPurchaseDescription')}
                    multiPurchaseTotalDescription={t('ProductDetail.multiPurchaseTotalDescription')}
                  />
                </div>
              )}

            <div className='col-span-1 mt-6 flex justify-center sm:mt-8 lg:col-span-7 lg:col-start-6 lg:justify-start'>
              <Button
                disabled={confirmDisabled}
                onClick={() => openProductActivationModal()}
                rounded
                data-testid='product-confirm-btn'
              >
                {confirmBtnText}
              </Button>
            </div>
            {productActivationModalVisible && (
              <PortalDialog
                ptHeader='py-3'
                onHide={() => {
                  setProductActivationModalVisible(false)
                  resetProductActivationModal()
                }}
                footerProps={{
                  okCallBack: () => onConfirmProductActivationModal(),
                  okLabel: t('ProductDetail.productActivationModal.okBtnLabel'),
                  okDisabled:
                    productActivationModalConfirmDisabled ||
                    (getFinalUsageList(selectedVariant, props.apiConfiguration) &&
                      getFinalUsageList(selectedVariant, props.apiConfiguration).length > 0 &&
                      !finalUsageIsSelected),
                  cancelCallback: () => {
                    setProductActivationModalVisible(false)
                    resetProductActivationModal()
                  },
                  cancelLabel: t('ProductDetail.productActivationModal.cancelBtnLabel'),
                }}
              >
                {/* <div>BENEFICIARIO DELLA RICHIESTA</div> */}
                <ProductActivationModal
                  typeOfBeneficiaries={selectedVariant?.targetPerson as TargetPersonEnum | undefined}
                  isMultiple={selectedVariant?.targetPersonMultiple}
                  user={membershipResponse?.data}
                  product={productResponse.data}
                  finalUsageList={getFinalUsageList(selectedVariant, props.apiConfiguration)}
                  personalData={selectedVariant?.personalData}
                  personalDataDisclaimer={selectedVariant?.personalDataDisclaimer}
                  apiConfiguration={props.apiConfiguration}
                  targetPersonDisclaimer={selectedVariant?.targetPersonDisclaimer}
                  familyMemberOptionValue={familyMemberOptionValue}
                  setFamilyMemberOptionValue={setFamilyMemberOptionValue}
                  errorMsg={errorMsg}
                  renderAnagraficaBtn={renderAnagraficaBtn}
                  finalUsage={finalUsage}
                  setFinalUsage={setFinalUsage}
                  finalUsageIsSelected={finalUsageIsSelected}
                  setFinalUsageIsSelected={setFinalUsageIsSelected}
                  onChangeFinalUsage={onChangeFinalUsage}
                  privacyItems={privacyItems}
                  setPrivacyItems={setPrivacyItems}
                  setProductActivationModalConfirmDisabled={setProductActivationModalConfirmDisabled}
                />
              </PortalDialog>
            )}
            {errorModalVisible && (
              <PortalDialog
                headerProps={{
                  title: t('ProductDetail.errorModal.title'),
                }}
                onHide={() => setErrorModalVisible(false)}
                footerProps={{
                  okCallBack: () => setErrorModalVisible(false),
                  okLabel: t('ProductDetail.errorModal.btnOkLabel'),
                }}
              >
                {errorMsg}
              </PortalDialog>
            )}
          </div>
        </div>
      )}
    </PageWidget>
  )
}
